import { useState } from 'react';
import { useForm } from 'react-hook-form';
import { Spinner } from '@/components/common/Spinner';
import { ErrorState } from '@/components/common/ErrorState';
import { EmptyState } from '@/components/common/EmptyState';
import { RowActionButtons } from '@/components/tables/RowActionButtons';
import { Modal } from '@/components/dialogs/Modal';
import { ConfirmDialog } from '@/components/dialogs/ConfirmDialog';
import { TextField } from '@/components/forms/TextField';
import { useToast } from '@/components/common/toast';
import { ApiError } from '@/api/client/abpError';
import type { TenantConnectionStringRow } from '@/api/services/tenants';
import {
  useConnectionStrings,
  useCreateConnectionString,
  useDeleteConnectionString,
  useUpdateConnectionString,
} from '../api';

interface CsForm {
  name: string;
  connectionString: string;
}
type ModalState = { mode: 'create' } | { mode: 'edit'; row: TenantConnectionStringRow } | null;

export function ConnectionStringsTab({ tenantId }: { tenantId: string }) {
  const toast = useToast();
  const listQuery = useConnectionStrings(tenantId);
  const createMut = useCreateConnectionString(tenantId);
  const updateMut = useUpdateConnectionString(tenantId);
  const deleteMut = useDeleteConnectionString(tenantId);

  const [modal, setModal] = useState<ModalState>(null);
  const [deleteTarget, setDeleteTarget] = useState<TenantConnectionStringRow | null>(null);

  const { register, handleSubmit, reset } = useForm<CsForm>();

  function openCreate() {
    reset({ name: '', connectionString: '' });
    setModal({ mode: 'create' });
  }
  function openEdit(row: TenantConnectionStringRow) {
    reset({ name: row.name, connectionString: row.connectionString });
    setModal({ mode: 'edit', row });
  }

  function onSubmit(values: CsForm) {
    const done = {
      onSuccess: () => {
        toast.success('Connection string saved.');
        setModal(null);
      },
      onError: (err: unknown) =>
        toast.error(err instanceof ApiError ? err.message : 'Failed to save connection string.'),
    };
    if (modal?.mode === 'create') {
      createMut.mutate({ name: values.name.trim(), connectionString: values.connectionString }, done);
    } else if (modal?.mode === 'edit') {
      updateMut.mutate({ name: modal.row.name, connectionString: values.connectionString }, done);
    }
  }

  function confirmDelete() {
    if (!deleteTarget) return;
    deleteMut.mutate(deleteTarget.name, {
      onSuccess: () => {
        toast.success('Connection string deleted.');
        setDeleteTarget(null);
      },
      onError: (err) => {
        toast.error(err instanceof ApiError ? err.message : 'Failed to delete.');
        setDeleteTarget(null);
      },
    });
  }

  if (listQuery.isLoading) return <Spinner />;
  if (listQuery.isError) return <ErrorState onRetry={() => listQuery.refetch()} />;

  const rows = listQuery.data?.items ?? [];
  const busy = createMut.isPending || updateMut.isPending;

  return (
    <div>
      <div className="d-flex justify-content-end mb-3">
        <button type="button" className="btn btn-success" title="Create new record" onClick={openCreate}>
          <i className="fa fa-plus me-1" />
          Create New
        </button>
      </div>

      <div className="table-responsive">
      <table className="table dataTable table-striped table-hover w-100 mb-0">
        <thead>
          <tr>
            <th className="text-start">Name</th>
            <th className="text-start">Connection String</th>
            <th>Actions</th>
          </tr>
        </thead>
        <tbody>
          {rows.length === 0 ? (
            <tr>
              <td colSpan={3}>
                <EmptyState message="No connection strings." />
              </td>
            </tr>
          ) : (
            rows.map((row) => (
              <tr key={row.name}>
                <td className="text-start">{row.name}</td>
                <td className="text-start">{row.connectionString}</td>
                <td>
                  <RowActionButtons onEdit={() => openEdit(row)} onDelete={() => setDeleteTarget(row)} />
                </td>
              </tr>
            ))
          )}
        </tbody>
      </table>
      </div>

      {modal && (
        <Modal
          show
          title={modal.mode === 'create' ? 'Create Connection String' : 'Edit Connection String'}
          onClose={() => setModal(null)}
          footer={
            <>
              <button type="button" className="btn btn-dark" onClick={() => setModal(null)} disabled={busy}>
                Cancel
              </button>
              <button type="submit" form="cs-form" className="btn btn-primary" disabled={busy}>
                {busy ? 'Saving…' : 'Save'}
              </button>
            </>
          }
        >
          <form id="cs-form" onSubmit={handleSubmit(onSubmit)} noValidate>
            {/* Name is the key — read-only on edit, matching legacy readOnlyFields. */}
            <TextField label="Name" disabled={modal.mode === 'edit'} {...register('name')} />
            <TextField label="Connection String" {...register('connectionString')} />
          </form>
        </Modal>
      )}

      <ConfirmDialog
        show={deleteTarget !== null}
        title="Delete connection string"
        message={`Delete "${deleteTarget?.name ?? ''}"?`}
        confirmLabel="Delete"
        busy={deleteMut.isPending}
        onConfirm={confirmDelete}
        onCancel={() => setDeleteTarget(null)}
      />
    </div>
  );
}
